// ════════════════════════════════════════════════════════════════════════
// Store timera skupienia — stan trzymany na poziomie MODUŁU (nie w Reakcie),
// dzięki czemu przeżywa remont komponentu i nawigację między lekcjami/zakładkami
// (wzorzec jak upload-manager-store). Odliczanie jest OPARTE NA CZASIE (epoch),
// więc odświeżenie strony i powrót po przerwie odtwarzają poprawny stan.
//
// Jeden centralny interwał = nigdy nie powstaje wiele równoległych timerów.
// ════════════════════════════════════════════════════════════════════════

import { DEFAULT_BREAK_MIN, DEFAULT_FOCUS_MIN, PRE_BREAK_WINDOW_MIN } from "@/lib/focus-plan";

export type FocusMode = "idle" | "focus" | "break";
export type FocusStatus = "running" | "paused";

export interface FocusContext {
  courseSlug: string;
  courseId: string;
  courseTitle: string;
  lessonId: string;
  lessonTitle: string;
  returnHref: string;
  subjectLabel: string; // np. „Matematyka • Lekcja 4"
}

export interface FocusSummary {
  focusedMs: number;
  breakMs: number;
  breaksTaken: number;
  context: FocusContext | null;
}

export interface FocusState {
  id: string;
  mode: FocusMode;
  status: FocusStatus;
  // Odliczanie bieżącej fazy (focus albo break)
  segmentStartedAt: number; // epoch ms — początek biegnącego segmentu
  baseElapsedMs: number; // czas uzbierany przed bieżącym segmentem (ta faza)
  durationMs: number; // zaplanowana długość bieżącej fazy
  // Konfiguracja sesji
  autoBreaks: boolean;
  focusMs: number;
  breakMs: number;
  // Liczniki
  breaksTaken: number;
  focusedMsTotal: number; // rzeczywisty czas skupienia w tej sesji
  // Flagi UI
  preBreakNotifiedOnce: boolean; // czy już wykryliśmy okno „5 min do końca"
  preBreakActive: boolean; // czy pokazać delikatne powiadomienie (można zamknąć)
  awaitingBreakChoice: boolean; // koniec skupienia — czekamy na wybór ucznia
  breakDone: boolean; // przerwa doliczyła do zera — ekran pokazuje „gotowy?"
  // Kontekst nauki (dokąd wrócić po przerwie)
  context: FocusContext | null;
  // Właściciel sesji (id konta ucznia) — izolacja między kontami na tej samej przeglądarce
  ownerId: string | null;
  // Sygnały jednorazowe konsumowane przez UI
  sessionEndSignal: number;
  lastSummary: FocusSummary | null;
}

const STORAGE_KEY = "ns_focus_timer_v1";

function makeId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `focus-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
}

function idleState(): FocusState {
  return {
    id: "",
    mode: "idle",
    status: "paused",
    segmentStartedAt: 0,
    baseElapsedMs: 0,
    durationMs: 0,
    autoBreaks: true,
    focusMs: DEFAULT_FOCUS_MIN * 60000,
    breakMs: DEFAULT_BREAK_MIN * 60000,
    breaksTaken: 0,
    focusedMsTotal: 0,
    preBreakNotifiedOnce: false,
    preBreakActive: false,
    awaitingBreakChoice: false,
    breakDone: false,
    context: null,
    ownerId: null,
    sessionEndSignal: 0,
    lastSummary: null,
  };
}

// Niezmienny snapshot dla SSR (useSyncExternalStore).
const SERVER_SNAPSHOT: FocusState = idleState();

let state: FocusState = idleState();
let hydrated = false;
// Id konta bieżącego ucznia — ustawiane przez reconcileFocusOwner (z /api/auth/me).
let currentOwnerId: string | null = null;
const listeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | null = null;

// ── Odczyt pochodnych ─────────────────────────────────────────────
export function getElapsedMs(s: FocusState): number {
  if (s.mode === "idle") return 0;
  const running = s.status === "running" ? Date.now() - s.segmentStartedAt : 0;
  return Math.max(0, s.baseElapsedMs + running);
}

export function getRemainingMs(s: FocusState): number {
  if (s.mode === "idle") return 0;
  return Math.max(0, s.durationMs - getElapsedMs(s));
}

// ── Subskrypcja ───────────────────────────────────────────────────
export function subscribeFocus(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getFocusSnapshot(): FocusState {
  if (!hydrated) hydrate();
  return state;
}

export function getFocusServerSnapshot(): FocusState {
  return SERVER_SNAPSHOT;
}

function commit(next: Partial<FocusState>) {
  state = { ...state, ...next };
  persist();
  listeners.forEach((l) => l());
}

// „Dotknięcie" — nowa referencja stanu, żeby countdown odświeżał się co sekundę.
function touch() {
  state = { ...state };
  listeners.forEach((l) => l());
}

// ── Persystencja (localStorage) ───────────────────────────────────
function persist() {
  if (typeof window === "undefined") return;
  try {
    if (state.mode === "idle") {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  } catch {
    /* ignore */
  }
}

function hydrate() {
  hydrated = true;
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as FocusState;
    if (parsed && (parsed.mode === "focus" || parsed.mode === "break")) {
      state = { ...idleState(), ...parsed };
      // Rozlicz czas, który upłynął, gdy karta była zamknięta.
      evaluate();
      ensureTicker();
    }
  } catch {
    /* ignore */
  }
}

// ── Centralny interwał + przejścia faz ────────────────────────────
function ensureTicker() {
  if (ticker || typeof window === "undefined") return;
  ticker = setInterval(() => {
    if (state.mode === "idle" || state.status !== "running") {
      stopTicker();
      return;
    }
    evaluate();
    touch(); // odśwież countdown
  }, 1000);
}

function stopTicker() {
  if (ticker) {
    clearInterval(ticker);
    ticker = null;
  }
}

// Rozlicza bieżącą fazę: powiadomienia i automatyczne przejścia po dojściu do zera.
function evaluate() {
  if (state.mode === "focus" && state.status === "running") {
    const remaining = getRemainingMs(state);
    if (
      state.autoBreaks &&
      remaining > 0 &&
      remaining <= PRE_BREAK_WINDOW_MIN * 60000 &&
      !state.preBreakNotifiedOnce
    ) {
      state = { ...state, preBreakNotifiedOnce: true, preBreakActive: true };
    }
    if (remaining <= 0) {
      // Koniec zaplanowanego skupienia — zamroź na planowanej wartości.
      state = { ...state, status: "paused", baseElapsedMs: state.durationMs, preBreakActive: false };
      if (state.autoBreaks) {
        state = { ...state, awaitingBreakChoice: true };
      } else {
        // Bez automatycznych przerw — po prostu zakończ sesję (bez wymuszania przerwy).
        finishSession(true);
      }
      stopTicker();
    }
  } else if (state.mode === "break" && state.status === "running") {
    const remaining = getRemainingMs(state);
    if (remaining <= 0) {
      state = { ...state, status: "paused", baseElapsedMs: state.durationMs, breakDone: true };
      stopTicker();
    }
  }
}

// Dopisz czas skupienia bieżącej fazy focus do sumy sesji (jednokrotnie na fazę).
function bankFocusElapsed() {
  if (state.mode !== "focus") return;
  const elapsed = getElapsedMs(state);
  state = { ...state, focusedMsTotal: state.focusedMsTotal + elapsed, baseElapsedMs: 0 };
}

// ── Akcje publiczne ───────────────────────────────────────────────
export interface StartFocusArgs {
  focusMs: number;
  breakMs: number;
  autoBreaks: boolean;
  context?: FocusContext | null;
}

export function startFocus({ focusMs, breakMs, autoBreaks, context }: StartFocusArgs) {
  if (!hydrated) hydrate();
  const now = Date.now();
  const dur = Math.max(60000, focusMs);
  state = {
    ...idleState(),
    id: makeId(),
    mode: "focus",
    status: "running",
    segmentStartedAt: now,
    baseElapsedMs: 0,
    durationMs: dur,
    autoBreaks,
    focusMs: dur,
    breakMs: Math.max(60000, breakMs),
    context: context ?? state.context ?? null,
    ownerId: currentOwnerId,
  };
  persist();
  listeners.forEach((l) => l());
  ensureTicker();
}

// ── Izolacja między kontami (na tej samej przeglądarce) ───────────
// Wywoływane, gdy znamy zalogowanego ucznia (np. z /api/auth/me). Sesja jest
// „przypisana" do konta: sesja innego konta jest kasowana, sesja bez właściciela
// przejmowana, a sesja tego samego konta zostaje (wznawia się po ponownym logowaniu).
export function reconcileFocusOwner(userId: string | null) {
  currentOwnerId = userId;
  if (!hydrated) hydrate();
  if (state.mode === "idle") return;
  if (!userId) {
    // Gość / wylogowany — nie pokazuj sesji przypisanej do jakiegoś konta.
    if (state.ownerId) clearForAccountSwitch();
    return;
  }
  if (state.ownerId && state.ownerId !== userId) {
    clearForAccountSwitch();
    return;
  }
  if (!state.ownerId) {
    state = { ...state, ownerId: userId };
    persist();
    listeners.forEach((l) => l());
  }
}

// Twarde wyczyszczenie trybu skupienia przy zmianie konta (bez logowania sesji).
function clearForAccountSwitch() {
  stopTicker();
  state = idleState();
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem("ns_focus_theme");
      window.localStorage.removeItem("ns_focus_sound");
      window.dispatchEvent(new Event("ns:focus-theme-changed"));
      window.dispatchEvent(new Event("ns:focus-sound-changed"));
    } catch {
      /* ignore */
    }
  }
  listeners.forEach((l) => l());
}

export function pauseFocus() {
  if (state.mode === "idle" || state.status !== "running") return;
  const elapsed = getElapsedMs(state);
  commit({ status: "paused", baseElapsedMs: elapsed });
  stopTicker();
}

export function resumeFocus() {
  if (state.mode === "idle" || state.status === "running") return;
  // Po dojściu do zera „wznów" nie ma sensu — najpierw trzeba przedłużyć fazę.
  if (getRemainingMs(state) <= 0) return;
  commit({ status: "running", segmentStartedAt: Date.now() });
  ensureTicker();
}

// „Jeszcze X min" / własny czas — przedłuża bieżącą fazę i wznawia, jeśli stała.
export function extendCurrentPhase(minutes: number) {
  if (state.mode === "idle") return;
  const add = Math.max(1, Math.round(minutes)) * 60000;
  const wasAtEnd = getRemainingMs(state) <= 0;
  state = {
    ...state,
    durationMs: state.durationMs + add,
    awaitingBreakChoice: false,
    breakDone: false,
    preBreakActive: false,
  };
  // Jeśli faza była zatrzymana na końcu — wystartuj nowy biegnący segment.
  if (wasAtEnd || state.status === "paused") {
    state = { ...state, status: "running", segmentStartedAt: Date.now(), preBreakNotifiedOnce: false };
  }
  persist();
  listeners.forEach((l) => l());
  ensureTicker();
}

// „Kontynuuj" po zakończeniu skupienia — bankuje odcinek i startuje nowy o tej samej długości.
export function continueFocus() {
  if (state.mode !== "focus") return;
  bankFocusElapsed();
  state = {
    ...state,
    status: "running",
    segmentStartedAt: Date.now(),
    baseElapsedMs: 0,
    durationMs: state.focusMs,
    awaitingBreakChoice: false,
    preBreakActive: false,
    preBreakNotifiedOnce: false,
  };
  persist();
  listeners.forEach((l) => l());
  ensureTicker();
}

export function dismissPreBreak() {
  if (!state.preBreakActive) return;
  commit({ preBreakActive: false });
}

export function startBreak(minutes?: number) {
  if (state.mode === "idle") return;
  if (state.mode === "focus") bankFocusElapsed();
  const dur = Math.max(60000, (minutes ? minutes * 60000 : state.breakMs));
  state = {
    ...state,
    mode: "break",
    status: "running",
    segmentStartedAt: Date.now(),
    baseElapsedMs: 0,
    durationMs: dur,
    breaksTaken: state.breaksTaken + 1,
    awaitingBreakChoice: false,
    breakDone: false,
    preBreakActive: false,
  };
  persist();
  listeners.forEach((l) => l());
  ensureTicker();
}

// Zakończ przerwę i wróć do nauki (lub pomiń) — kończy całą sesję i loguje.
export function endBreak() {
  finishSession(true);
}

export function skipBreak() {
  // Pomiń przerwę: nie licz jej jako odbytej.
  if (state.mode === "break") {
    state = { ...state, breaksTaken: Math.max(0, state.breaksTaken - 1) };
  }
  finishSession(true);
}

// „Zakończ" sesję skupienia (przycisk w widgetcie).
export function endFocus() {
  finishSession(true);
}

// „Reset" — ciche zakończenie bez podsumowania (ale z zalogowaniem realnego czasu).
export function resetFocus() {
  finishSession(false);
}

function finishSession(showSummary: boolean) {
  if (state.mode === "focus") bankFocusElapsed();
  const focusedMs = state.focusedMsTotal;
  const breaksTaken = state.breaksTaken;
  const breakMs = state.breakMs;
  const context = state.context;
  const plannedMs = state.focusMs;
  const completed = focusedMs >= state.focusMs * 0.95;

  stopTicker();

  const summary: FocusSummary | null = showSummary && focusedMs >= 30000 ? { focusedMs, breakMs, breaksTaken, context } : null;

  logSession({ plannedMs, focusedMs, breaksTaken, context, completed });

  state = {
    ...idleState(),
    // zachowaj kontekst nauki na potrzeby kolejnej sesji
    context,
    focusMs: state.focusMs,
    breakMs: state.breakMs,
    autoBreaks: state.autoBreaks,
    sessionEndSignal: summary ? state.sessionEndSignal + 1 : state.sessionEndSignal,
    lastSummary: summary,
  };
  persist();
  listeners.forEach((l) => l());
}

// Aktualizacja kontekstu (np. przy przejściu do innej lekcji), bez restartu timera.
export function setFocusContext(context: FocusContext) {
  if (!hydrated) hydrate();
  // Nie nadpisuj, jeśli nic się nie zmienia (unikamy zbędnych re-renderów).
  const c = state.context;
  if (
    c &&
    c.returnHref === context.returnHref &&
    c.lessonId === context.lessonId &&
    c.subjectLabel === context.subjectLabel
  ) {
    return;
  }
  commit({ context });
}

// ── Logowanie sesji do API (best-effort, client-only) ─────────────
interface LogArgs {
  plannedMs: number;
  focusedMs: number;
  breaksTaken: number;
  completed: boolean;
  context: FocusContext | null;
}

function logSession({ plannedMs, focusedMs, breaksTaken, completed, context }: LogArgs) {
  if (typeof window === "undefined") return;
  if (focusedMs < 30000) return;
  try {
    void fetch("/api/auth/focus-sessions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        plannedMs,
        focusedMs,
        breaksTaken,
        completed,
        courseId: context?.courseId || undefined,
        lessonId: context?.lessonId || undefined,
        courseTitle: context?.courseTitle || undefined,
      }),
    }).catch(() => {});
    window.dispatchEvent(new Event("ns:focus-session-logged"));
  } catch {
    /* ignore */
  }
}
